<template>
    <div class="card rv-page">
        <PageHeader
            :title="pageTitle"
            subtitle="Check the lines of this version, then approve or reject it"
            @onClickCloseButton="navigateToList"
        />

        <div class="rv-body" :class="{ 'is-loading': pageLoading }">
            <FormSection title="Version Details" icon="fa-solid fa-clipboard-check">
                <template #actions>
                    <button type="button" class="btn rv-btn rv-btn-ghost" @click="historyOpen = true">
                        <i class="fa-solid fa-clock-rotate-left"></i><span>History</span>
                    </button>
                    <button type="button" class="btn rv-btn rv-btn-ghost" :disabled="!! exportingVersionId || ! version.id" @click="onExportVersion(version)">
                        <span v-if="exportingVersionId === version.id" class="spinner-border spinner-border-sm"></span>
                        <span v-if="exportingVersionId !== version.id"><i class="fa-solid fa-download"></i></span>
                        <span>Export Version</span>
                    </button>
                </template>

                <div class="rv-grid">
                    <div class="rv-field">
                        <span class="rv-label">Master Budget Line</span>
                        <span class="rv-value">{{ version.budget_line_name }}</span>
                    </div>
                    <div class="rv-field">
                        <span class="rv-label">Budget Allocation</span>
                        <span class="rv-value">{{ version.budget_name }}</span>
                        <span v-if="version.budget_code" class="rv-sub">{{ version.budget_code }}</span>
                    </div>
                    <div class="rv-field">
                        <span class="rv-label">Version</span>
                        <span class="rv-value">V{{ version.version_no }} <StatusChip :status="version.status" :tones="budgetVersionTones"/></span>
                        <span class="rv-sub">{{ budgetVersionKind(version.version_no) }}</span>
                    </div>
                    <div class="rv-field">
                        <span class="rv-label">Total Amount</span>
                        <span class="rv-value">{{ commaFormat(version.total_amount) }}</span>
                        <span class="rv-sub" :class="changeClass">{{ changeLabel }}</span>
                    </div>
                    <div class="rv-field">
                        <span class="rv-label">Submitted</span>
                        <span class="rv-value">{{ formatDateTimeLabel(version.submitted_at) }}</span>
                        <span class="rv-sub">{{ version.submitted_by_name }}</span>
                    </div>
                    <div class="rv-field">
                        <span class="rv-label">Created By</span>
                        <span class="rv-value">{{ version.created_by_name }}</span>
                    </div>
                    <div v-if="version.approved_by_name" class="rv-field">
                        <span class="rv-label">Approved</span>
                        <span class="rv-value">{{ formatDateTimeLabel(version.approved_at) }}</span>
                        <span class="rv-sub">{{ version.approved_by_name }}</span>
                    </div>
                    <div v-if="version.status === 'rejected'" class="rv-field">
                        <span class="rv-label">Rejected</span>
                        <span class="rv-value">{{ formatDateTimeLabel(version.updated_at) }}</span>
                        <span class="rv-sub">{{ version.decided_by_name }}</span>
                    </div>
                    <div v-if="version.note" class="rv-field rv-field-wide">
                        <span class="rv-label">Note</span>
                        <span class="rv-value rv-note">{{ version.note }}</span>
                    </div>
                </div>
            </FormSection>

            <FormSection title="Budget Heads" icon="fa-solid fa-sitemap">
                <div class="bt-tree" :class="{ 'is-loading': treeLoading }">
                    <AccountTree
                        :key="tab + treeKey"
                        :groupData="projectAccountList.group"
                        :endpointData="projectAccountList.endpoint"
                        :accountType="tab"
                        :tabs="tabs"
                        :isExporting="headSheet.isExporting"
                        :fetchTree="fetchTreeChildren"
                        :canImport="false"
                        @handleTabChange="handleTabChange"
                        @handleRootPagination="getAccountTree"
                        @handleExport="headSheet.exportSheet()"
                    />
                </div>
            </FormSection>
        </div>

        <div class="rv-footer">
            <span v-if="footerNote" class="rv-footer-note">{{ footerNote }}</span>
            <button type="button" class="btn rv-btn rv-btn-ghost" @click="navigateToList">Back to List</button>
            <button v-if="canDecide" type="button" class="btn rv-btn rv-btn-danger" :disabled="busy" @click="rejectOpen = true">
                <i class="fa-solid fa-xmark"></i><span>Reject</span>
            </button>
            <button v-if="canDecide" type="button" class="btn rv-btn rv-btn-success" :disabled="busy" @click="decide('approve', null)">
                <span v-if="busy" class="spinner-border spinner-border-sm"></span>
                <span v-if="! busy"><i class="fa-solid fa-check"></i></span><span>Approve</span>
            </button>
        </div>

        <BudgetGeneralModal
            ref="budgetGeneralModal"
            :key="budgetGeneralForm.project_account_id"
            :form="budgetGeneralForm"
            :accountType="tab"
            :readonly="true"
        />

        <VersionHistoryModal
            :isOpen="historyOpen"
            :versions="versions"
            :exportingVersionId="exportingVersionId"
            @close="historyOpen = false"
            @export="onExportVersion"
        />

        <RejectModal
            :isOpen="rejectOpen"
            :versionLabel="'V' + version.version_no"
            :busy="busy"
            @close="rejectOpen = false"
            @submit="note => decide('reject', note)"
        />
    </div>
</template>

<script setup>
import { computed, inject, nextTick, onMounted, onUnmounted, reactive, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import PageHeader from '@/components/atom/PageHeader.vue'
import FormSection from '@/components/atom/FormSection.vue'
import StatusChip from '@/components/atom/StatusChip.vue'
import AccountTree from '@/components/molecule/procurement/budget/AccountTree'
import BudgetGeneralModal from '@/components/molecule/procurement/budget/BudgetGeneralModal'
import VersionHistoryModal from '@/components/molecule/procurement/budget-tracker/VersionHistoryModal.vue'
import RejectModal from '@/components/molecule/procurement/budget-tracker/RejectModal.vue'
import handleBudgetTracker from '@/services/modules/procurement/budgetTracker'
import handleDropdowns from '@/services/modules/dropdowns'
import useBudgetHeadSheet from '@/composables/useBudgetHeadSheet'
import useBudgetVersionExport from '@/composables/useBudgetVersionExport'
import figureFormatter from '@/services/utils/figureFormatter'
import useAutoRefresh from '@/composables/useAutoRefresh'
import { budgetVersionKind, budgetVersionTones, formatDateTimeLabel, getApiErrorMessage } from '@/services/utils/global.js'

const $route = useRoute()
const $router = useRouter()
const showError = inject('showError')
const showSuccess = inject('showSuccess')
const emitter = inject('emitter')
const { fetchApprovalVersion, decideBudgetVersion, fetchTrackerTree, fetchTrackerLine } = handleBudgetTracker()
const { fetchDropdownList } = handleDropdowns()
const { exportingVersionId, exportVersion } = useBudgetVersionExport(() => $route.params.companyId)
const { commaFormat } = figureFormatter()

const budgetGeneralModal = ref(null)
const pageLoading = ref(false)
const treeLoading = ref(false)
const busy = ref(false)
const historyOpen = ref(false)
const rejectOpen = ref(false)
const tab = ref('account')
const treeKey = ref(0)
const homeContent = ref({})
const version = ref({})
const versions = ref([])
const budgetGeneralForm = ref({ project_account_id: '' })
const projectAccountList = ref({ group: [], endpoint: {} })

const budgetId = computed(() => $route.params.budgetId)

const versionId = computed(() => Number($route.params.versionId))

const companyQuery = computed(() => `?company_id=${$route.params.companyId}`)

const pageTitle = computed(() => version.value.version_no ? `Review V${version.value.version_no} - ${version.value.budget_name}` : 'Review Budget')

const isPending = computed(() => version.value.status === 'pending')

const canDecide = computed(() => isPending.value)

const decidedStatus = {
    approve: 'active',
    reject: 'rejected'
}

const statusNotes = {
    active: 'Approved - this is now the active budget.',
    superseded: 'Approved earlier - a newer version has replaced it.',
    rejected: 'Rejected - sent back to the creator to change and resubmit.',
    draft: 'Back in draft with the creator.',
    cancelled: 'This revision was discarded.'
}

const footerNote = computed(() => {
    if (! version.value.status) return ''
    if (! isPending.value) return statusNotes[version.value.status] || `This version is ${version.value.status}.`
    return ''
})

const approvedStatuses = ['active', 'superseded']

const previousVersion = computed(() => versions.value.find(item => item.version_no < version.value.version_no && approvedStatuses.includes(item.status)))

const changeAmount = computed(() => {
    if (! previousVersion.value) return 0
    return Number(version.value.total_amount || 0) - Number(previousVersion.value.total_amount || 0)
})

const changeLabel = computed(() => {
    if (! previousVersion.value) return 'New budget'
    const previousLabel = 'V' + previousVersion.value.version_no
    if (! changeAmount.value) return `No change from ${previousLabel}`
    const sign = changeAmount.value > 0 ? '+' : '-'
    return `${sign}${commaFormat(Math.abs(changeAmount.value))} vs ${previousLabel}`
})

const changeClass = computed(() => {
    if (! changeAmount.value) return ''
    return changeAmount.value > 0 ? 'rv-up' : 'rv-down'
})

const tabQuery = () => {
    return companyQuery.value + `&account_type=${tab.value}`
}

const tabs = computed(() => {
    const types = homeContent.value.budget_head_type
    if (! types) return []
    return Object.keys(types).map(key => ({ id: key, name: types[key] }))
})

const currentTab = computed(() => tabs.value.find(item => item.id === tab.value) || tabs.value[0])

const headSheet = reactive(useBudgetHeadSheet(
    () => budgetId.value,
    () => tabQuery() + `&version_id=${versionId.value}`,
    () => version.value.budget_name,
    () => currentTab.value,
    () => getAccountTree()
))

const navigateToList = () => {
    const params = Object.assign({}, $route.params)
    delete params.budgetId
    delete params.versionId
    $router.push({ name: 'budget-tracker-approval', params, query: Object.assign({ status: 'pending' }, $route.query) })
}

let decisionNo = 0

const applyVersion = (data) => {
    version.value = data.version
    versions.value = data.versions
}

const getVersion = async () => {
    const res = await fetchApprovalVersion(budgetId.value, versionId.value, companyQuery.value)
    if (! res.status) return showError(res.message)
    applyVersion(res.data)
}

const refreshVersion = async () => {
    const res = await fetchApprovalVersion(budgetId.value, versionId.value, companyQuery.value)
    if (! res.status) return
    applyVersion(res.data)
}

const refreshSilently = async () => {
    if (busy.value) return
    const startedAt = decisionNo
    const res = await fetchApprovalVersion(budgetId.value, versionId.value, companyQuery.value)
    if (busy.value || startedAt !== decisionNo) return
    if (! res.status) return
    applyVersion(res.data)
}

useAutoRefresh(refreshSilently, () => isPending.value)

const getAccountTree = async (page = 1) => {
    treeLoading.value = true
    try {
        const res = await fetchTrackerTree(budgetId.value, tabQuery() + `&version_id=${versionId.value}&page=${page}&offset=10`)
        if (! res.status) return showError(res.message)
        projectAccountList.value = { group: res.data.group, endpoint: res.data.endpoint }
    } catch (err) {
        showError(getApiErrorMessage(err))
    } finally {
        treeLoading.value = false
    }
}

const fetchTreeChildren = (query) => fetchTrackerTree(budgetId.value, query + `&version_id=${versionId.value}`)

const handleTabChange = (tabName) => {
    tab.value = tabName
    getAccountTree()
}

const onDecisionRefused = async (message) => {
    showError(message)
    await refreshVersion()
    if (isPending.value) return
    rejectOpen.value = false
}

const decide = async (action, note) => {
    busy.value = true
    decisionNo++
    try {
        const res = await decideBudgetVersion(budgetId.value, versionId.value, { action, note }, companyQuery.value)
        if (! res.status) return await onDecisionRefused(res.message)
        showSuccess(res.message)
        rejectOpen.value = false
        version.value = Object.assign({}, version.value, { status: decidedStatus[action], note: note || version.value.note })
        await refreshVersion()
    } catch (err) {
        showError(getApiErrorMessage(err))
    } finally {
        busy.value = false
    }
}

const onExportVersion = (item) => {
    exportVersion(budgetId.value, version.value.budget_name, item)
}

const openBudgetLine = async (headId) => {
    try {
        const res = await fetchTrackerLine(budgetId.value, companyQuery.value + `&project_account_id=${headId}&version_id=${versionId.value}`)
        if (! res.status) return showError(res.message)
        budgetGeneralForm.value = Object.assign({}, res.data)
        await nextTick()
        budgetGeneralModal.value.openModal()
    } catch (err) {
        showError(getApiErrorMessage(err))
    }
}

const getDropdowns = async () => {
    const res = await fetchDropdownList(companyQuery.value + '&is_home=1')
    if (! res.status || ! res.data) return
    homeContent.value = res.data.home_data
}

const onBudgetGeneralEdit = ({ data }) => {
    openBudgetLine(data.data.id)
}

onMounted(async () => {
    pageLoading.value = true
    try {
        await Promise.all([getDropdowns(), getVersion()])
        await getAccountTree()
    } catch (err) {
        showError(getApiErrorMessage(err))
    } finally {
        pageLoading.value = false
    }
})

emitter.off('onBudgetGeneralEdit')
emitter.on('onBudgetGeneralEdit', onBudgetGeneralEdit)

onUnmounted(() => {
    emitter.off('onBudgetGeneralEdit', onBudgetGeneralEdit)
})
</script>

<style scoped>
.rv-page {
    min-height: calc(100vh - 158px);
}
.rv-body {
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 14px 16px;
    transition: opacity .15s ease;
}
.rv-body.is-loading {
    opacity: .55;
    pointer-events: none;
}
.rv-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 16px 20px;
}
.rv-field {
    display: flex;
    flex-direction: column;
    gap: 2px;
}
.rv-field-wide {
    grid-column: 1 / -1;
}
.rv-label {
    font-size: 12px;
    font-weight: 600;
    letter-spacing: .04em;
    text-transform: uppercase;
    color: #667085;
}
.rv-value {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 15px;
    font-weight: 600;
    color: #101828;
}
.rv-sub {
    font-size: 13px;
    color: #667085;
}
.rv-note {
    font-weight: 400;
    white-space: pre-wrap;
}
.rv-up {
    color: #b54708;
    font-weight: 600;
}
.rv-down {
    color: #067647;
    font-weight: 600;
}
.bt-tree {
    font-size: 14px;
    transition: opacity .15s ease;
}
.bt-tree.is-loading {
    opacity: .5;
}
.rv-footer {
    position: sticky;
    bottom: 0;
    z-index: 5;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
    flex-wrap: wrap;
    padding: 12px 16px;
    border-top: 1px solid #eaecf0;
    border-radius: 0 0 .428rem .428rem;
    background: #fff;
    box-shadow: 0 -4px 12px rgba(16, 24, 40, .05);
}
.rv-footer-note {
    margin-right: auto;
    font-size: 14px;
    color: #475467;
}
.rv-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    height: 40px;
    min-width: 96px;
    padding: 0 18px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    white-space: nowrap;
}
.rv-btn:disabled {
    opacity: .6;
    cursor: not-allowed;
}
.rv-btn-ghost {
    background: #fff;
    border: 1px solid #e4e7ec;
    color: #475467;
}
.rv-btn-ghost:hover:not(:disabled) {
    background: #f9fafb;
    color: #101828;
}
.rv-btn-success {
    background: #067647;
    border: 1px solid #067647;
    color: #fff;
}
.rv-btn-success:hover:not(:disabled) {
    background: #05603a;
    color: #fff;
}
.rv-btn-danger {
    background: #fff;
    border: 1px solid #fecdca;
    color: #b42318;
}
.rv-btn-danger:hover:not(:disabled) {
    background: #fef3f2;
    color: #b42318;
}
</style>
